import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, Truck, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [transporterDropdownOpen, setTransporterDropdownOpen] = useState(false);
  const location = useLocation();

  const transporterLinks = [
    { 
      name: 'GoMore - Transporter', 
      url: 'https://gomore.ch/mietauto/2328470?place_ref=google-ChIJbaYMOi73mkcR8ta5PmLku_I&place_name=Kreuzlingen' 
    },
    { 
      name: '2EM - Iveco Daily', 
      url: 'https://de.2em.ch/autovermietung/kreuzlingen/iveco-daily-35c-16-11606' 
    }
  ];

  const navLinks = [
    { path: '/', label: 'Start' },
    { path: '/about', label: 'Über uns' },
    { path: '/services', label: 'Dienstleistungen' },
    { path: '/contact', label: 'Kontakt' },
    { path: '/agb', label: 'AGB' },
    { path: '/impressum', label: 'Impressum' },
    { path: '/datenschutz', label: 'Datenschutz' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="w-full">
      {/* Top Bar */}
      <div className="bg-[#1e3a5f] text-white py-2">
        <div className="max-w-7xl mx-auto px-4 flex justify-center md:justify-end items-center gap-6 text-sm">
          <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Phone className="w-4 h-4" />
            <span>{companyInfo.phone}</span>
          </a>
          <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Mail className="w-4 h-4" />
            <span>{companyInfo.email}</span>
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src={companyInfo.logo} 
                alt="Helper-Schweiz Logo" 
                className="h-16 md:h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[15px] font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-[#c41e3a] border-b-2 border-[#c41e3a] pb-1'
                      : 'text-[#1e3a5f] hover:text-[#c41e3a]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <div className="relative">
                <button 
                  onClick={() => setTransporterDropdownOpen(!transporterDropdownOpen)}
                  onBlur={() => setTimeout(() => setTransporterDropdownOpen(false), 150)}
                  className="flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#152a47] text-white px-5 py-2 rounded-full font-medium transition-colors"
                >
                  <Truck className="w-4 h-4" />
                  Transporter mieten
                  <ChevronDown className={`w-4 h-4 transition-transform ${transporterDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {transporterDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-3 min-w-[260px] z-50">
                    <p className="px-4 pb-2 text-xs text-gray-500 border-b mb-2">
                      Mein Transporter kann ausschliesslich über diese Plattformen gemietet werden:
                    </p>
                    {transporterLinks.map((link) => (
                      <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-[#f0f5fa] transition-colors text-[#1e3a5f] font-medium text-sm"
                      >
                        <Truck className="w-4 h-4 text-[#c41e3a]" />
                        {link.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/contact">
                <Button className="bg-[#c41e3a] hover:bg-[#a31830] text-white px-6 py-2 rounded-full">
                  Jetzt anfragen
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-[#1e3a5f]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t pt-4">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-[15px] font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-[#c41e3a]'
                        : 'text-[#1e3a5f] hover:text-[#c41e3a]'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="bg-[#c41e3a] hover:bg-[#a31830] text-white w-full rounded-full">
                    Jetzt anfragen
                  </Button>
                </Link>
                <div className="border-t pt-4 mt-2">
                  <p className="text-xs text-gray-500 mb-3">Mein Transporter kann ausschliesslich über diese Plattformen gemietet werden:</p>
                  {transporterLinks.map((link) => (
                    <a 
                      key={link.url}
                      href={link.url}
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#152a47] text-white px-5 py-2 rounded-full font-medium transition-colors mb-2"
                    >
                      <Truck className="w-4 h-4" />
                      {link.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
